import type { BoardOptions } from '@portal/shared';

export type BoardFilter = NonNullable<BoardOptions['filter']> | 'all';

/** Keep old callers and the default board on the same cache entry. */
export function boardOptions(value: BoardOptions | BoardFilter = {}): BoardOptions {
  const options = typeof value === 'string' ? (value === 'all' ? {} : { filter: value }) : value;
  return Object.fromEntries(
    Object.entries(options).filter(
      ([key, entry]) =>
        entry !== undefined && entry !== '' && !(key === 'sort' && entry === 'updated'),
    ),
  );
}

export function boardDateError(options: BoardOptions): string | null {
  if (options.submittedFrom && options.submittedTo && options.submittedFrom > options.submittedTo) {
    return 'Submitted from must be on or before Submitted to.';
  }
  return null;
}

export function hasBoardFilters(options: BoardOptions): boolean {
  return Object.keys(boardOptions(options)).some((key) => key !== 'sort');
}

export function activeBoardFilters(
  options: BoardOptions,
): Array<{ key: keyof BoardOptions; label: string }> {
  const labels = {
    filter: {
      mine: 'Submitted by me',
      feature: 'Features',
      maintenance: 'Fixes',
      no_charge: 'No charge',
      needs_attention: 'Needs input',
    },
    kind: { feature: 'Features', maintenance: 'Fixes', needs_triage: 'Needs triage' },
    attention: { any: 'Needs input', decision: 'Needs a decision', reply: 'Needs a reply' },
    billing: { billable: 'Billable', no_charge: 'No charge' },
  };
  const chips: Array<{ key: keyof BoardOptions; label: string }> = [];
  for (const key of ['filter', 'kind', 'attention', 'billing'] as const) {
    const value = options[key];
    if (value) chips.push({ key, label: (labels[key] as Record<string, string>)[value] ?? value });
  }
  if (options.submittedFrom)
    chips.push({ key: 'submittedFrom', label: `From ${options.submittedFrom} UTC` });
  if (options.submittedTo)
    chips.push({ key: 'submittedTo', label: `Through ${options.submittedTo} UTC` });
  if (options.q) chips.push({ key: 'q', label: `Search: ${options.q}` });
  return chips;
}

const SUBMITTED_DAY = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** Match the UTC calendar days used by submission filters. */
export function boardDate(value: string): string {
  const date = new Date(value);
  // During a rolling release an older API may briefly omit the new submitted timestamp.
  // Show its absence rather than substituting the update date or crashing the whole board.
  return Number.isNaN(date.getTime()) ? '—' : SUBMITTED_DAY.format(date);
}
